/**
 * Lightweight fuzzy matcher tuned for the mobile search overlay.
 *
 * Goals:
 *   - "tok" matches "tiktok" and "monetizing on TikTok in canada"
 *   - small typos tolerated ("shopfy" → "shopify")
 *   - very fast — runs against ~70 strings on every keystroke
 *
 * Strategy:
 *   1. Token-based substring + prefix matching for instant strong matches.
 *   2. Levenshtein distance ≤2 on individual tokens for typo tolerance.
 *   3. Score = lower is better. Used to rank in addition to filter.
 */
function levenshtein(a: string, b: string, max = 2): number {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (Math.abs(al - bl) > max) return max + 1;
  if (al === 0) return bl;
  if (bl === 0) return al;
  let prev = new Array(bl + 1);
  let cur = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= bl; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return max + 1;
    [prev, cur] = [cur, prev];
  }
  return prev[bl];
}

export function fuzzyScore(needle: string, haystack: string): number {
  const n = needle.toLowerCase().trim();
  if (!n) return 0;
  const h = haystack.toLowerCase();

  // Strongest: exact substring of full haystack.
  const idx = h.indexOf(n);
  if (idx >= 0) {
    // Earlier matches rank higher; word-boundary matches rank highest.
    const isWordBoundary = idx === 0 || /\s/.test(h[idx - 1]);
    return (isWordBoundary ? 0 : 1) + idx * 0.001;
  }

  // Per-token analysis: query tokens vs haystack tokens.
  const queryTokens = n.split(/\s+/).filter(Boolean);
  const hayTokens = h.split(/\s+/).filter(Boolean);
  let total = 0;
  for (const qt of queryTokens) {
    let best = Infinity;
    for (const ht of hayTokens) {
      if (ht.startsWith(qt)) { best = Math.min(best, 1); continue; }
      if (ht.includes(qt)) { best = Math.min(best, 2); continue; }
      // Allow tiny edit distance for typo tolerance, only on similar-length tokens.
      if (Math.abs(ht.length - qt.length) <= 2) {
        const d = levenshtein(qt, ht, 2);
        if (d <= 2) best = Math.min(best, 3 + d);
      }
    }
    if (best === Infinity) return Infinity;
    total += best;
  }
  return 5 + total;
}

export function fuzzyMatch(needle: string, haystack: string): boolean {
  return fuzzyScore(needle, haystack) !== Infinity;
}
